import * as moment from 'moment';
// import * as momentTimezone from 'moment-timezone';
import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';
import { MortgageAddress, CommonAddress } from '../models/index';
import { UnityLoanOfficer, UnityBranchOffice } from '../../shared/models/index';

@Injectable()
export class Utils {
  private static currencyPipe: CurrencyPipe = new CurrencyPipe('en-US');

  static MILLISECONDS_IN_YEAR = 31556952000;

  /**
   * This method will drop all prototype information about this object.
   * Only use this method on objects without added functions.
   */
  static cloneObjectNoProto<T>(obj: T): T {
    const out = JSON.parse(JSON.stringify(obj));

    return out;
  }

  static calculateMillisecondDiffFromToday(datetime: string): number {
    let selection = new Date(datetime);
    let offset = selection.getTimezoneOffset() * 60 * 1000;
    selection = new Date(selection.getTime() + offset);
    selection.setHours(0, 0, 0, 0);
    let today = new Date();
    today.setHours(0, 0, 0, 0);

    return selection.getTime() - today.getTime();
  }

  static normalizeEventKey(event: KeyboardEvent): string {
    if (event.key) {
      return event.key;
    }

    switch (event.keyCode) {
      case 8: return 'Backspace';
      case 9: return 'Tab';
      case 13: return 'Enter';
      case 27: return 'Escape';
      case 33: return 'PageUp';
      case 34: return 'PageDown';
      case 37: return 'ArrowLeft';
      case 38: return 'ArrowUp';
      case 39: return 'ArrowRight';
      case 40: return 'ArrowDown';
      default: return String.fromCharCode(event.keyCode);
    }
  }

  static calculateDifferenceBetweenDates(startDate: string, endDate: string): number {
    let startDateDate = new Date(startDate);
    let endDateDate = new Date(endDate);

    return endDateDate.getTime() - startDateDate.getTime();
  }

  static calculateDayDiffFromToday(datetime: string): number {
    return Utils.calculateMillisecondDiffFromToday(datetime) / (1000 * 60 * 60 * 24);
  }

  // converts a string in the format $123,456.99 to the number value 123456.99
  static currencyStringToNumber(currencyString: string): number {

    let output: number;

    if (typeof currencyString === 'string') {
      output = currencyString ? +currencyString.replace(/\$|,/g, '') : 0;
    } else {
      output = +currencyString;
    }

    return output;
  }

  // removes circular references in objects
  static JSONStringify(o): string {
    let cache = [];
    const result = JSON.stringify(o, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (cache.indexOf(value) !== -1) {
          // Circular reference found, discard key
          return;
        }
        // Store value in our collection
        cache.push(value);
      }
      return value;
    });

    return result;
  }

  static capitalizeSentence(sentence: string): string {
    return Utils.splitOnPeriod(sentence).map(piece => piece.trim().charAt(0).toUpperCase() + piece.trim().slice(1)).join('. ');
  }

  static splitOnPeriod(sentence: string): string[] {
    let result = [];
    let str = '';
    let i = 0;

    while (i < sentence.length) {
      if (sentence[i] === '.') {
        // ignore decimal points in numeric values (e.g. don't split '9.99' into '9' and '99'
        if (Utils.isStringNumber(sentence[i + 1]) && Utils.isStringNumber(sentence[i + 2])) {
          str += sentence[i];
        } else if (sentence[i - 1] === 'e' &&
                   sentence[i + 1] === 'g' &&
                   sentence[i + 2] === '.') {
          str += sentence[i] + sentence[i + 1] + sentence[i + 2];

          i += 2;
        } else {
          result.push(str);
          str = '';
        }
      } else {
        str += sentence[i];
      }

      i++;
    }

    result.push(str);

    return result;
  }

  static isStringNumber(char: string) {
    return Number(char).toString() === char;
  }

  static formatFloat(number, numberDecimalPlaces) {
    return parseFloat((Math.round(number * 10 ** numberDecimalPlaces) / 10 ** numberDecimalPlaces).toFixed(numberDecimalPlaces));
  }

  // converts a number to a correctly formatted currency.
  // utilizes the currencyPipe.transform() method
  public static numberToCurrencyString(
    value: number,
    numberDecimalPlaces: 0|2|4): string {

      // We don't want to return null in the case that we get a value like 0.
      // We do want to return null for values like undefined or null.
      if (value == null) {
        return null;
      }

      let valueStr = value.toString().replace(/[^\d\.]/g, '') || '';
      if (!!valueStr) {
        const indexOfDecimal = valueStr.indexOf('.');
        if (indexOfDecimal > -1) {
          valueStr = valueStr.slice(0, indexOfDecimal + numberDecimalPlaces + 1);
        }
        const digitFormat = `1.0-${numberDecimalPlaces}`;

        // Test for a string starting with a decimal point, followed by
        // any number of digits.
        if (/^\.\d*/.test(valueStr)) {

          // Fix for CurrencyPipe expected value to be a valid representation of
          // a number, where starting the input value with a decimal point would
          // normally throw an invalidPipeArgumentError (defined in Angular's NumberPipe.)
          valueStr = `0${ valueStr }`;
        }
        valueStr = Utils.currencyPipe.transform(valueStr, 'USD', 'symbol', digitFormat);

        // LDTSULA-3006: the first push of decimal point should bring up $0.
        if (valueStr === '$0' && value !== 0) {
          valueStr += '.';
        }
      }

      return valueStr;
  }

  public static formatAddress(address: MortgageAddress): string {
    let compositeAddress: string = '';

    const insertBetween = (before: string, after: string, insertion: string) => {
      if (before.length > 0) {
        return before + insertion + after;
      } else {
        return after;
      }
    };

    if (address) {
      if (address.number && address.street) {
        compositeAddress += `${address.number} ${address.street}`;
      } else {
        // used || operator to weed out undefined values
        compositeAddress += address.street || address.number || '';
      }

      if (address.city) {
        compositeAddress = insertBetween(compositeAddress, address.city, ', ');
      }

      if (address.state) {
        compositeAddress = insertBetween(compositeAddress, address.state, ', ');
      }

      if (address.zipCode) {
        compositeAddress = insertBetween(compositeAddress, address.zipCode, ' ');
      }
    }
    return compositeAddress;
  }

  static parseAddressForStreetAndName(address): { number; street; } {
    if (address == null) {
      return null;
    }

    let addressSplit = address.split(' ');
    const potentialNumber = addressSplit.shift();
    const street = addressSplit.join(' ');
    const hasStreetNumber: boolean = !isNaN(potentialNumber);
    return {
      number: hasStreetNumber ? potentialNumber : null,
      street: hasStreetNumber ? street : potentialNumber + ' ' + street
    };
  }

  static flattenAddressModel(address: CommonAddress): string {
    return Utils.flattenAddressProperties(
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.state,
      address.zipCode);
  }

  static flattenAddressProperties(
    addressLine1: string,
    addressLine2: string,
    city: string,
    state: string,
    zipCode: string): string {
    let a = addressLine1;
    let au = addressLine2;
    let c = city;
    let s = state;
    let z = zipCode;

    let useCommaAfterAddress = a && (c || s || z);
    let useCommaAfterCity = c && s;
    let useSpaceAfterState = z ? true : false;

    // Intended case: (all optional) address addressUnit, city, state zip
    let addressStr =
      (a || '') + (au ? ' ' + au : '') + (useCommaAfterAddress ? ', ' : '') +
      (c || '') + (useCommaAfterCity ? ', ' : '') + (s || '') + (useSpaceAfterState ? ' ' : '') + (z || '');
    return addressStr;
  }

  static extractDateFromDateTime(date: string): string {
    let newDate: string = '';

    if (date === null || typeof date === 'undefined') {
      newDate = null;
    } else if (date !== '' && date.indexOf('T') > 0) {
      newDate = date.split('T')[0];
    } else {
      newDate = '';
    }

    return newDate;
  }

  static getDateString(date: Date) {
    const dateString: string = !date ? '' : moment.utc(date).format('MM/DD/YYYY');
    return dateString;
  }

  // TODO: Remove this hard-coding per https://han.loandepot.com/browse/LDTSULA-1926
  // The /runcredit endpoint will return a 400 if it doesn't have a street number. Per Fred, this is a limitation of EPS.
  // and the above linked bug was created to track that work. Per Fred, we're also hard-coding the street number
  // to 0 for addresses like "D Bothell Everett Hwy, Bothell, WA 98012" (i.e. addresses that have a number === null).
  static CONDITIONALLY_HARD_CODE_STREET_NUMBER(streetNumber: string): string {
    if (!streetNumber) {
      streetNumber = '0';
      console.error('Hard-coding the street number to \'0\' because it\'s null and /runcredit will blow up if we don\'t have it');
    }

    return streetNumber;
  }

  // TODO: Remove this hard-coding per https://han.loandepot.com/browse/LDTSULA-1926
  // Due to the above issue, the address number will sometimes be hard-coded as 0.
  // Handle that case when displaying address information in the last step.
  static CONDITIONALLY_DECODE_STREET_NUMBER(streetNumber: string): string {
    if (streetNumber === '0') {
      streetNumber = '';
      console.error('Handling hard-coded street number for co-borrower when no information was provided.');
    }

    return streetNumber;
  }

  // Note: This function intentionally *only returns numbers* and not things like decimals.
  // Do not modify this to also return other values
  static getOnlyDigitsFromString(value: any): any {
    if (typeof(value) === 'string') {
      return value.replace(/[^\d]/g, '');
    } else {
      return value;
    }
  }

  private static formatPhoneNumberHelper(phoneNumber: string, spaceAfterParen: boolean): string {
    if (!phoneNumber) {
      return '';
    }

    let strippedNumber = Utils.getOnlyDigitsFromString(phoneNumber);
    let formattedPhoneNumber: string = '';

    // chop off extra numbers because QA
    if (strippedNumber && strippedNumber.length > 10) {
      strippedNumber = strippedNumber.substr(0, 10);
    }

    // don't accidentally reformat international or weird phone numbers
    if (strippedNumber) {
      for (let x = 0; x < strippedNumber.length; x++) {
        if (x === 0) {
          formattedPhoneNumber += '(' + strippedNumber[x];
        } else if (x === 3) {
          const parenString = spaceAfterParen ? ') ' : ')';
          formattedPhoneNumber += parenString + strippedNumber[x];
        } else if (x === 6) {
          formattedPhoneNumber += '-' + strippedNumber[x];
        } else {
          formattedPhoneNumber += strippedNumber[x];
        }
      }
    } else if (phoneNumber.replace(/[( ]/g, '').length === 0) {
      formattedPhoneNumber = '';
    } else {
      // reset to original number if things are weird
      formattedPhoneNumber = phoneNumber;
    }

    return formattedPhoneNumber;
  }

  // Note: The front end should specify phone numbers as (###) ###-####,
  // so use this method when displaying data on the front end.
  // When sending information to the backend/API, use formatPhoneNumberForBackEnd
  static formatPhoneNumber(phoneNumber: string): string {
    return this.formatPhoneNumberHelper(phoneNumber, true);
  }

  static formatPhoneNumberForBackEnd(phoneNumber: string): string {
    return this.formatPhoneNumberHelper(phoneNumber, false);
  }

  static formatSSN(ssn: string): string {
    if (!ssn) {
      return ssn;
    }

    ssn = (ssn || '').replace(/[^\d*]/g, '');
    let formattedSSN: string = '';

    if (ssn && ssn.length) {
      for (let x = 0; x < ssn.length; x++) {
        if (x === 3 || x === 5) {
          formattedSSN += '-' + ssn[x];
        } else {
          formattedSSN += ssn[x];
        }
      }
    } else {
      formattedSSN = ssn;
    }

    return formattedSSN;
  }

  public static getIEVersion(): number {
    const sAgent = window.navigator.userAgent;
    const Idx = sAgent.indexOf('MSIE');

    // If IE, return version number.
    if (Idx > 0) {
      return parseInt(sAgent.substring(Idx + 5, sAgent.indexOf('.', Idx)), 10);

    // If IE 11 then look for Updated user agent string.
    } else if (!!navigator.userAgent.match(/Trident\/7\./)) {
      return 11;

    // It is not IE
    } else {
      return 0;
    }
  }

  public static isBrowserIE(): boolean {
    return this.getIEVersion() > 0;
  }

  // given a form and the name of a control whose input should be a currency string,
  // returns the number value (e.g. "$1,000" becomes 1000)
  controlCurrencyValue(form: FormGroup, controlId: string): number {
    let control: AbstractControl = null;
    if (form && form.controls) {
      control = form.controls[controlId];
    }
    // Make sure we clean out the currency string garbage in order
    // to form an actual number value.
    return control && control.valid ? Utils.currencyStringToNumber(control.value) : 0;
  }

  // use to calculate a percent from 0-100, not 0-1
  // will round values over 100 down to 100,
  // and defaults to 100 if numerator is less than 0
  calculatePercent(numerator: number, denominator: number): number {
    const fraction: number = numerator > 0 ? (denominator / numerator) * 100 : 0;
    return (numerator > 0 && numerator <= denominator) ?
      100 : this.truncValueByDecimalPlaces(fraction, 2);
  }

  // We expect the server to sent us a date in UTC
  // The server expects the user's *local time* in UTC
  localTimeInUTCToDatePickerDate(localTimeInUTC: string): string {
    if (localTimeInUTC === null || typeof localTimeInUTC === 'undefined') {
      return localTimeInUTC;
    }

    let datePickerDate: string = '';

    // TODO: Revisit this because we're just chopping off the time which will probably break something at some point
    if (localTimeInUTC !== null && typeof localTimeInUTC === 'string' && localTimeInUTC.indexOf('T') > 0) {
      datePickerDate = localTimeInUTC.split('T')[0];
    }

    return datePickerDate;
  }

  // // The server expects the user's *local time* in UTC
  // datePickerDateToLocalTimeInUTC(datePickerDate: string): string {
  //   if (datePickerDate === null || typeof datePickerDate === 'undefined') {
  //     return datePickerDate;
  //   }
  //
  //   if (datePickerDate !== null && typeof datePickerDate === 'string' && datePickerDate.indexOf('T') > 0) {
  //     datePickerDate = datePickerDate.split('T')[0];
  //   }
  //
  //   let dateParts: string[] = datePickerDate.split('-');
  //   if (dateParts[0].length > 4) {
  //     datePickerDate = dateParts[0].substr(0, 4) + '-' + dateParts[1] + '-' + dateParts[2]; // don't let user enter absurd years
  //   }
  //
  //   let dateString: string = momentTimezone.tz(datePickerDate, moment.tz.guess()).format(); // add the user's timezone to the date
  //   if (dateString === 'Invalid date') {
  //     dateString = '';
  //   }
  //
  //   if (dateString !== '') {
  //     // convert it to the same format backend uses (otherwise the form is always dirty)
  //     dateString = new Date(dateString).toISOString();
  //
  //     // chop off milliseconds since backend doesn't send it and it really should be .000Z
  //     dateString = dateString.split('.000Z').join('');
  //   }
  //
  //   return dateString;
  // }

  getParsableFloatFromCurrencyString(value: any): any {
    if (typeof(value) === 'string') {
      return value.replace(/[^\d.]/g, '');
    } else {
      return value;
    }
  }

  //
  // // Note: We need to format phone numbers the same way Empower does
  // formatPhoneNumberWithExt(phoneNumber: string): string {
  //   let strippedNumber = this.getOnlyDigitsFromString(phoneNumber);
  //   let formattedPhoneNumber: string = '';
  //
  //   // chop off extra numbers because QA
  //   if (strippedNumber && strippedNumber.length > 20) {
  //     strippedNumber = strippedNumber.substr(0, 20);
  //   }
  //
  //   // don't accidentally reformat international or weird phone numbers
  //   if (strippedNumber) {
  //     for (let x = 0; x < strippedNumber.length; x++) {
  //       if (x === 0) {
  //         formattedPhoneNumber += '(' + strippedNumber[x];
  //       } else if (x === 2) {
  //         formattedPhoneNumber += strippedNumber[x] + ')';
  //       } else if (x === 5) {
  //         formattedPhoneNumber += strippedNumber[x] + '-';
  //       } else if (x === 9) {
  //         formattedPhoneNumber += strippedNumber[x] + ' Ext. ';
  //       } else {
  //         formattedPhoneNumber += strippedNumber[x];
  //       }
  //     }
  //   } else {
  //     formattedPhoneNumber = phoneNumber; // reset to original number if things are weird
  //   }
  //
  //   return formattedPhoneNumber;
  // }

  formatZipCode(zip: string): string {
    let strippedNumber = Utils.getOnlyDigitsFromString(zip);
    let formattedZip: string = '';

    // chop off extra numbers because QA
    if (strippedNumber && strippedNumber.length > 9) {
      strippedNumber = strippedNumber.substr(0, 9);
    }

    if (strippedNumber) {
      for (let x = 0; x < strippedNumber.length; x++) {
        if (x === 5) {
          formattedZip += '-' + strippedNumber[x];
        } else {
          formattedZip += strippedNumber[x];
        }
      }
    } else {
      formattedZip = zip; // reset new number on overwrite
    }

    return formattedZip;
  }

  parseImplicitUtcString(date: string): Date { // adds UTC timezone information to date string
    if (!date) {
      return null;
    }
    try {
      let m = moment.utc(date);
      if (!m.isValid()) {
        return null;
      }
      return m.toDate();
    } catch (e) {
      return null;
    }
  }

  transformDateIntoUtcString(date: Date): string {
    if (!date) {
      return null;
    }

    try {
      let m = moment.utc(date);

      if (!m.isValid()) {
        return null;
      }

      return m.format();
    } catch (e) {
      return null;
    }
  }

  // parseHouseNumberFromStreet(street: string): string {
  //   if (!_.isString(street)) return null;
  //   let matches = street.match(/[0-9]+/g);
  //   return matches ? (matches[0] || null) : null;
  // }

  convertToMomentTimezone(timezone: string): string {
    // todo: enum/constants for API time zones
    switch (timezone) {
      case 'Alaska Time Zone':
        return 'America/Anchorage';
      case 'Central Time Zone':
        return 'America/Matamoros';
      case 'Eastern Time Zone':
        return 'America/New_York';
      case 'Hawaii-Aleutian Time Zone':
        return 'Pacific/Honolulu';
      case 'Mountain Time Zone':
        return 'America/Boise';
      case 'Pacific Time Zone':
        return 'America/Los_Angeles';
      case 'PST':
        return 'America/Los_Angeles';
      default:
        return 'America/Los_Angeles';
    }
  }

  requiredFieldClass(value: any) {
    if (value === null || typeof value === 'undefined') {
      return true;
    } else if (typeof value === 'string' && value === '' || value === ' ') {
      return true;
    }
    return false;
  }

  convertNumberValue(value: any) {
    // keep unedited values the same
    if (value === null || typeof value === 'undefined' || value === '') {
      return value;
    }

    let num: number = parseFloat(value);

    if (isNaN(num)) {
      return value; // return original value if the converted value isn't a number
    } else {
      return num;
    }
  }

  convertNumberValueToString(value: any): string {
    // keep unedited values the same
    if (value === null || typeof value === 'undefined') {
      return value;
    }

    return value.toString();
  }

  roundToNearestPenny(value: number): number {
    if (!value) {
      return null;
    }
    return Math.round(value * 100) / 100;
  }

  roundToOneDecimal(value: number): number {
    if (!value) {
      return null;
    }
    return parseFloat((Math.round((value) * 10) / 10).toFixed(1) as any);
  }

  getMonthsBetweenMoments(startMoment: moment.Moment, endMoment: moment.Moment) {
    if (!startMoment || !startMoment.isValid() || !endMoment || !endMoment.isValid()) {
      return null;
    }

    let getAbsoluteMonths = (momentDate) => {
      let months = parseInt(momentDate.format('MM') as any, 10);
      let years = parseInt(momentDate.format('YYYY') as any, 10);
      return months + (years * 12);
    };

    let startMonths = getAbsoluteMonths(startMoment);
    let endMonths = getAbsoluteMonths(endMoment);

    let months = endMonths - startMonths;
    return months;
  }

  // Note: state refers to a state in an address
  // ensure value is null if it's empty so default value shows in dropdown
  formatStateFromLoan(state: string): string {
    if (state === '' || typeof state === 'undefined') {
      return null;
    } else {
      return state;
    }
  }

  // ensure value is an empty string if it's empty so default value shows in dropdown
  formatCountryFromLoan(country: string): string {
    if (country === null || typeof country === 'undefined') {
      return ''; // country must be an empty string to default to United States
    } else {
      return country;
    }
  }

  formatStateToLoan(originalState: string, newState: string): string {
    // only update state if it changed
    // TODO: There is probably an opportunity here for a more concise if-statement
    if (originalState === null || typeof originalState === 'undefined') {
      if (newState !== null) {
        return newState;
      } else {
        return originalState;
      }
    } else {
      return newState;
    }
  }

  formatCountryToLoan(originalCountry: string, newCountry: string): string {
    // only update country if it changed
    // TODO: There is probably an opportunity here for a more concise if-statement
    if (originalCountry === null || typeof originalCountry === 'undefined') {
      if (newCountry !== '') {
        return newCountry;
      } else {
        return originalCountry;
      }
    } else {
      return newCountry;
    }
  }

  dateYearsAgoFromToday(years: number): Date {
    // If the date is within the last 2 years, then we show another section that asks: Address and "Date you started living here".
    let today = new Date();
    let dateNYearsAgo = new Date(today.setFullYear(today.getFullYear() - years));
    return dateNYearsAgo;
  }

  exactDateYearsAgoFromDate(date: Date, years: number): Date {
    const millisecondsToSubtract = years * Utils.MILLISECONDS_IN_YEAR;
    const dateNYearsAgo = new Date(date.setMilliseconds(date.getUTCMilliseconds() - millisecondsToSubtract));
    return dateNYearsAgo;
  }

  // For instances where something like: 99.99890009949 needs to be shown
  // as 99.99, but toFixed(2) rounds it to 100.
  // For example: 50000/50001 = 0.9999800004
  // (50000/50001).toFixed(2) = 100 (not desired)
  // truncValueByDecimalPlaces(50000/50001, 2) = 99.99 (desired)
  truncValueByDecimalPlaces(value: number, decimalPlaces: number): number {
    const regex = new RegExp('(\\d+\\.\\d{' + decimalPlaces + '})(\\d)');
    const matches = value.toString().match(regex);
    return matches ? parseFloat(matches[1]) : value.valueOf();
  };

  titleCaseSentence(sentence: string): string {
    let str = sentence.toLowerCase().split(' ').map(word => {
      return (word.charAt(0).toUpperCase() + word.slice(1));
    });
    return str.join(' ');
  }

  /* If loanOfficer exists, display loanOfficer phone number;
     OR, if loanofficerbranch exists, display loanofficerbranch phone number;
     OR, if branch exist, display branch phone number;
     If no phone number exists and default phone number passed in, display default number */
  getPhoneNumber(
    loanOfficer: UnityLoanOfficer,
    loanOfficerBranch: UnityBranchOffice,
    branch?: UnityBranchOffice,
    defaultNumber?: string): string {

    if (loanOfficer && (loanOfficer.phoneNumber1 || loanOfficer.phoneNumber2) ) {
      return loanOfficer.phoneNumber1 || loanOfficer.phoneNumber2;
    } else if (branch && branch.phoneNumber) {
      return branch.phoneNumber;
    } else {
      return defaultNumber;
    }
  }
}
